/** Reusable image gallery — viewer + full editor — for any parent that exposes
 *  an `ImageGalleryApi` (activities, itineraries; see `@/api/media`).
 *
 *  The component OWNS its data: a `useQuery({ queryKey, queryFn: api.list })` plus
 *  mutations that invalidate `queryKey` on success. It is a self-contained,
 *  immediate-sync sub-resource — nothing here couples to any parent draft/dirty
 *  state, which is exactly why it can drop into the itinerary editor without
 *  touching that page's combined-save machinery.
 *
 *  Flows:
 *   - Reorder: optimistic cache write of the new order, then `api.reorder(ids)`;
 *     on error the previous snapshot is restored and an error snackbar shown.
 *   - Upload: batch `api.upload(files)` from the dropzone / "Add images" button.
 *   - Replace / Crop: the drawer produces a File → `api.replace(id, file)`.
 *   - Delete: single (card) or bulk (toolbar), each gated behind `ConfirmDialog`.
 *
 *  `canManage=false` renders a read-only viewer: thumbnail grid + lightbox only. A manager's
 *  toolbar says the gallery is saved immediately (the bolt of an immediate action): on a
 *  record page it is outside the draft, and Save never commits it. */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DotSeparated } from '@/components/DotSeparated';
import { useSnackbar } from '@/components/Snackbar';

import { ImageDetailDrawer } from './ImageDetailDrawer';
import { ImageGalleryGrid } from './ImageGalleryGrid';
import { ImageLightbox } from './ImageLightbox';
import { UploadDropzone } from './UploadDropzone';
import type { GalleryImage, GalleryPatch, ImageGalleryProps } from './types';

type DeleteTarget = { kind: 'single'; image: GalleryImage } | { kind: 'bulk'; ids: number[] } | null;

export function ImageGallery({ api, queryKey, canManage, showInGroup = false }: ImageGalleryProps) {
  const qc = useQueryClient();
  const { show } = useSnackbar();

  const query = useQuery({ queryKey, queryFn: () => api.list() });
  const images = useMemo(() => query.data ?? [], [query.data]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<GalleryImage | null>(null);
  const [lightbox, setLightbox] = useState<GalleryImage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey });

  // Keep the open drawer's image in sync with the freshest list (after a
  // replace/save refetch) so the preview and defaults update in place.
  const editingLive = editing ? (images.find((i) => i.id === editing.id) ?? editing) : null;

  const uploadMut = useMutation({
    mutationFn: (files: File[]) => api.upload(files),
    onSuccess: (created) => {
      void invalidate();
      show(`${created.length} image${created.length === 1 ? '' : 's'} uploaded`, 'success');
    },
    onError: () => show('Upload failed', 'error'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: GalleryPatch }) => api.update(id, patch),
    onSuccess: () => {
      void invalidate();
      show('Image details saved', 'success');
    },
    onError: () => show('Could not save image details', 'error'),
  });

  const replaceMut = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => api.replace(id, file),
    onSuccess: () => {
      void invalidate();
      show('Image replaced', 'success');
    },
    onError: () => show('Could not replace image', 'error'),
  });

  const removeMut = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => api.remove(id))).then(() => ids),
    onSuccess: (ids) => {
      void invalidate();
      setSelectedIds(new Set());
      if (editing && ids.includes(editing.id)) setEditing(null);
      show(`${ids.length} image${ids.length === 1 ? '' : 's'} deleted`, 'success');
    },
    onError: () => {
      void invalidate();
      show('Could not delete image(s)', 'error');
    },
  });

  // Reorder: optimistic cache write with rollback on failure.
  const reorderMut = useMutation({
    mutationFn: (orderedIds: number[]) => api.reorder(orderedIds),
    onMutate: async (orderedIds: number[]) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<GalleryImage[]>(queryKey);
      if (previous) {
        const byId = new Map(previous.map((i) => [i.id, i]));
        const next = orderedIds
          .map((id) => byId.get(id))
          .filter((i): i is GalleryImage => i != null)
          .map((img, idx) => ({ ...img, sequence: idx }));
        qc.setQueryData<GalleryImage[]>(queryKey, next);
      }
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
      show('Could not save the new order', 'error');
    },
    onSuccess: (fresh) => {
      qc.setQueryData<GalleryImage[]>(queryKey, fresh);
      show('Order saved', 'success');
    },
    onSettled: () => void invalidate(),
  });

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const ids = deleteTarget.kind === 'single' ? [deleteTarget.image.id] : deleteTarget.ids;
    removeMut.mutate(ids);
    setDeleteTarget(null);
  };

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
        Couldn’t load images.
      </Alert>
    );
  }

  const selectedCount = selectedIds.size;
  const busy = uploadMut.isPending || removeMut.isPending || reorderMut.isPending;

  return (
    <Box>
      {canManage && (
        <>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
            flexWrap="wrap"
            useFlexGap
          >
            {/* Whole parts that wrap with no dot left at a line's end or start (390 px). */}
            <Typography variant="body2" color="text.secondary" component="div" sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <DotSeparated
                parts={[
                  `${images.length} image${images.length === 1 ? '' : 's'}`,
                  images.length > 1 && 'drag to reorder',
                  <span key="immediate">
                    <BoltOutlinedIcon aria-hidden sx={{ fontSize: 16, verticalAlign: '-3px', mr: 0.5 }} />
                    <span data-gallery-immediate>Saved immediately</span>
                  </span>,
                ]}
              />
            </Typography>
            {selectedCount > 0 && (
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteSweepIcon />}
                disabled={busy}
                onClick={() => setDeleteTarget({ kind: 'bulk', ids: Array.from(selectedIds) })}
              >
                Delete selected ({selectedCount})
              </Button>
            )}
          </Stack>

          <Box sx={{ mb: 2 }}>
            <UploadDropzone disabled={uploadMut.isPending} onFiles={(files) => uploadMut.mutate(files)} />
          </Box>
        </>
      )}

      {images.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {canManage ? 'No images yet — add some above.' : 'No images.'}
        </Typography>
      ) : (
        <ImageGalleryGrid
          images={images}
          canManage={canManage}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onReorder={(ids) => reorderMut.mutate(ids)}
          onEdit={(img) => setEditing(img)}
          onDelete={(img) => setDeleteTarget({ kind: 'single', image: img })}
          onOpen={(img) => setLightbox(img)}
        />
      )}

      <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} />

      <ImageDetailDrawer
        image={editingLive}
        open={editing != null}
        canManage={canManage}
        showInGroup={showInGroup}
        saving={updateMut.isPending}
        replacing={replaceMut.isPending}
        onClose={() => setEditing(null)}
        onSave={(patch) => editingLive && updateMut.mutate({ id: editingLive.id, patch })}
        onReplace={(file) => editingLive && replaceMut.mutate({ id: editingLive.id, file })}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete image(s)?"
        message={
          deleteTarget?.kind === 'bulk'
            ? `Permanently delete ${deleteTarget.ids.length} selected image(s)? This cannot be undone.`
            : 'Permanently delete this image? This cannot be undone.'
        }
        confirmLabel="Delete"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
